import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../axiosInstance';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import * as XLSX from 'xlsx';

function DataExport({ user }) {
    const [data, setData] = useState({
        fees: [],
        payroll: [],
        students: [],
        teachers: [],
        accountsReceivable: []
    });

    const [exportConfig, setExportConfig] = useState({
        exportType: 'sessions', // sessions, payroll, ytdPayroll, parentPayments, monthlyBreakdown
        format: 'excel', // excel or pdf
        dateRange: 'all', // all, year, month, custom
        year: new Date().getFullYear(),
        month: '',
        fromDate: '',
        toDate: '',
        teacher: '',
        student: '',
        parent: '',
        includeDetails: true,
        includeTotals: true,
        groupBy: 'none' // none, teacher, student, parent, month
    });

    const [isExporting, setIsExporting] = useState(false);

    const now = new Date();
    const currentYear = now.getFullYear();

    // Fetch all data
    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const fetchPromises = [
                    axiosInstance.get('/logs'),
                    axiosInstance.get('/students'),
                    axiosInstance.get('/teachers'),
                    axiosInstance.get('/payroll')
                ];

                if (user.role === 'admin') {
                    fetchPromises.push(axiosInstance.get('/receivables'));
                }

                const results = await Promise.all(fetchPromises);

                setData({
                    fees: results[0].data,
                    students: results[1].data,
                    teachers: results[2].data,
                    payroll: results[3].data,
                    accountsReceivable: results[4] ? results[4].data : []
                });
            } catch (err) {
                console.error('Failed to fetch data:', err);
                alert('Failed to load data for export');
            }
        };

        fetchAllData();
    }, [user.role]);

    // Helper functions
    const parseLocalDate = (dateStr) => {
        if (!dateStr) return new Date();
        if (dateStr.includes('T')) return new Date(dateStr);
        return new Date(dateStr + 'T12:00:00');
    };

    const getStudentByName = (name) => data.students.find(s => s.name === name);
    const getTeacherByName = (name) => data.teachers.find(t => t.name === name);

    const getStudentRateOnDate = (student, sessionDate) => {
        if (!student) return 0;
        const date = new Date(sessionDate);

        if (!student.rateHistory || student.rateHistory.length === 0) {
            return student.rate;
        }

        for (let i = student.rateHistory.length - 1; i >= 0; i--) {
            const historyEntry = student.rateHistory[i];
            const effectiveDate = new Date(historyEntry.effectiveDate);
            const endDate = historyEntry.endDate ? new Date(historyEntry.endDate) : new Date('2099-12-31');

            if (date >= effectiveDate && date <= endDate) {
                return historyEntry.rate;
            }
        }

        return student.rate;
    };

    const getTeacherSalaryOnDate = (teacher, sessionDate) => {
        if (!teacher) return 0;
        const date = new Date(sessionDate);

        if (!teacher.salaryHistory || teacher.salaryHistory.length === 0) {
            return teacher.salary;
        }

        for (let i = teacher.salaryHistory.length - 1; i >= 0; i--) {
            const historyEntry = teacher.salaryHistory[i];
            const effectiveDate = new Date(historyEntry.effectiveDate);
            const endDate = historyEntry.endDate ? new Date(historyEntry.endDate) : new Date('2099-12-31');

            if (date >= effectiveDate && date <= endDate) {
                return historyEntry.salary;
            }
        }

        return teacher.salary;
    };

    // Filter data based on configuration
    const getFilteredData = useMemo(() => {
        let filtered = [...data.fees];

        // Date filtering
        if (exportConfig.dateRange === 'year') {
            filtered = filtered.filter(f => parseLocalDate(f.date).getFullYear() === exportConfig.year);
        } else if (exportConfig.dateRange === 'month' && exportConfig.month) {
            const [year, month] = exportConfig.month.split('-').map(Number);
            filtered = filtered.filter(f => {
                const d = parseLocalDate(f.date);
                return d.getFullYear() === year && d.getMonth() + 1 === month;
            });
        } else if (exportConfig.dateRange === 'custom') {
            if (exportConfig.fromDate) {
                filtered = filtered.filter(f => parseLocalDate(f.date) >= parseLocalDate(exportConfig.fromDate));
            }
            if (exportConfig.toDate) {
                filtered = filtered.filter(f => parseLocalDate(f.date) <= parseLocalDate(exportConfig.toDate));
            }
        }

        // Entity filtering
        if (exportConfig.teacher) {
            filtered = filtered.filter(f => f.teacher === exportConfig.teacher);
        }
        if (exportConfig.student) {
            filtered = filtered.filter(f => f.student === exportConfig.student);
        }
        if (exportConfig.parent) {
            filtered = filtered.filter(f => {
                const student = getStudentByName(f.student);
                return student?.parent === exportConfig.parent;
            });
        }

        return filtered;
    }, [data.fees, data.students, exportConfig]);

    // Prepare sessions data for export
    const prepareSessionsData = () => {
        const sessions = getFilteredData.map(log => {
            const student = getStudentByName(log.student);
            const feePerHour = getStudentRateOnDate(student, log.date);
            const totalFees = feePerHour * log.hours;

            return {
                date: parseLocalDate(log.date).toLocaleDateString(),
                teacher: log.teacher,
                student: log.student,
                parent: student?.parent || 'N/A',
                hours: log.hours,
                feePerHour: feePerHour,
                totalFees: totalFees
            };
        });

        // Sort by date descending
        sessions.sort((a, b) => new Date(b.date) - new Date(a.date));

        return sessions;
    };

    // Prepare payroll data for export
    const preparePayrollData = () => {
        const payrollByTeacher = {};

        getFilteredData.forEach(f => {
            const teacher = getTeacherByName(f.teacher);
            const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);

            if (!payrollByTeacher[f.teacher]) {
                payrollByTeacher[f.teacher] = { hours: 0, totalPay: 0 };
            }

            payrollByTeacher[f.teacher].hours += f.hours;
            payrollByTeacher[f.teacher].totalPay += f.hours * historicalSalary;
        });

        return Object.entries(payrollByTeacher).map(([teacher, vals]) => ({
            teacher,
            hours: vals.hours,
            totalPay: vals.totalPay
        }));
    };

    // Prepare YTD payroll data
    const prepareYTDPayrollData = () => {
        const yearFees = data.fees.filter(f =>
            parseLocalDate(f.date).getFullYear() === exportConfig.year
        );

        const payrollByTeacher = {};

        yearFees.forEach(f => {
            const teacher = getTeacherByName(f.teacher);
            const historicalSalary = getTeacherSalaryOnDate(teacher, f.date);

            if (!payrollByTeacher[f.teacher]) {
                payrollByTeacher[f.teacher] = { hours: 0, totalPay: 0 };
            }

            payrollByTeacher[f.teacher].hours += f.hours;
            payrollByTeacher[f.teacher].totalPay += f.hours * historicalSalary;
        });

        // Calculate payments
        const yearPayments = data.payroll.filter(p =>
            parseLocalDate(p.date).getFullYear() === exportConfig.year
        );

        const paymentsByTeacher = {};
        yearPayments.forEach(p => {
            if (!paymentsByTeacher[p.teacher]) {
                paymentsByTeacher[p.teacher] = 0;
            }
            paymentsByTeacher[p.teacher] += p.totalPay;
        });

        return Object.entries(payrollByTeacher).map(([teacher, vals]) => ({
            teacher,
            hours: vals.hours,
            payable: vals.totalPay,
            paid: paymentsByTeacher[teacher] || 0,
            outstanding: vals.totalPay - (paymentsByTeacher[teacher] || 0)
        }));
    };

    // Prepare parent payments data
    const prepareParentPaymentsData = () => {
        const parentData = {};

        getFilteredData.forEach(f => {
            const student = getStudentByName(f.student);
            if (!student) return;

            const parent = student.parent;
            const feePerHour = getStudentRateOnDate(student, f.date);
            const amount = feePerHour * f.hours;
            const monthKey = parseLocalDate(f.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });

            if (!parentData[parent]) {
                parentData[parent] = {};
            }
            if (!parentData[parent][monthKey]) {
                parentData[parent][monthKey] = { billed: 0, sessions: [] };
            }

            parentData[parent][monthKey].billed += amount;
            parentData[parent][monthKey].sessions.push({
                date: parseLocalDate(f.date).toLocaleDateString(),
                student: student.name,
                hours: f.hours,
                amount: amount
            });
        });

        const rows = [];
        Object.entries(parentData).forEach(([parent, months]) => {
            Object.entries(months).forEach(([month, data]) => {
                rows.push({
                    parent,
                    month,
                    billed: data.billed,
                    sessions: data.sessions.length
                });
            });
        });

        return rows;
    };

    // Prepare monthly breakdown
    const prepareMonthlyBreakdown = () => {
        const yearFees = data.fees.filter(f =>
            parseLocalDate(f.date).getFullYear() === exportConfig.year
        );

        const months = [
            'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
        ];

        return months.map((monthName, idx) => {
            const monthFees = yearFees.filter(f => parseLocalDate(f.date).getMonth() === idx);

            const revenue = monthFees.reduce((sum, f) => {
                const student = getStudentByName(f.student);
                const rate = getStudentRateOnDate(student, f.date);
                return sum + (rate * f.hours);
            }, 0);

            const payroll = monthFees.reduce((sum, f) => {
                const teacher = getTeacherByName(f.teacher);
                const salary = getTeacherSalaryOnDate(teacher, f.date);
                return sum + (salary * f.hours);
            }, 0);

            const hours = monthFees.reduce((sum, f) => sum + f.hours, 0);
            const sessions = monthFees.length;

            return {
                month: monthName,
                sessions,
                hours,
                revenue,
                payroll,
                profit: revenue - payroll
            };
        });
    };

    // Export to Excel
    const exportToExcel = () => {
        let worksheetData = [];
        let filename = '';

        switch (exportConfig.exportType) {
            case 'sessions':
                worksheetData = prepareSessionsData();
                filename = `Sessions_${exportConfig.dateRange}_${Date.now()}.xlsx`;
                break;
            case 'payroll':
                worksheetData = preparePayrollData();
                filename = `Payroll_${exportConfig.dateRange}_${Date.now()}.xlsx`;
                break;
            case 'ytdPayroll':
                worksheetData = prepareYTDPayrollData();
                filename = `YTD_Payroll_${exportConfig.year}_${Date.now()}.xlsx`;
                break;
            case 'parentPayments':
                worksheetData = prepareParentPaymentsData();
                filename = `Parent_Payments_${exportConfig.dateRange}_${Date.now()}.xlsx`;
                break;
            case 'monthlyBreakdown':
                worksheetData = prepareMonthlyBreakdown();
                filename = `Monthly_Breakdown_${exportConfig.year}_${Date.now()}.xlsx`;
                break;
            default:
                alert('Invalid export type');
                return;
        }

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

        // Add totals if enabled
        if (exportConfig.includeTotals && worksheetData.length > 0) {
            const totalsRow = {};

            Object.keys(worksheetData[0]).forEach(key => {
                if (typeof worksheetData[0][key] === 'number') {
                    totalsRow[key] = worksheetData.reduce((sum, row) => sum + (row[key] || 0), 0);
                } else if (key === Object.keys(worksheetData[0])[0]) {
                    totalsRow[key] = 'TOTAL';
                } else {
                    totalsRow[key] = '';
                }
            });

            XLSX.utils.sheet_add_json(worksheet, [totalsRow], { skipHeader: true, origin: -1 });
        }

        // Download
        XLSX.writeFile(workbook, filename);
    };

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        let title = '';
        let headers = [];
        let rows = [];

        switch (exportConfig.exportType) {
            case 'sessions': {
                title = 'Student Sessions Report';
                const sessions = prepareSessionsData();
                headers = [['Date', 'Teacher', 'Student', 'Parent', 'Hours', 'Rate', 'Total']];
                rows = sessions.map(s => [
                    s.date,
                    s.teacher,
                    s.student,
                    s.parent,
                    s.hours.toFixed(2),
                    `$${s.feePerHour.toFixed(2)}`,
                    `$${s.totalFees.toFixed(2)}`
                ]);
                break;
            }
            case 'payroll': {
                title = 'Payroll Report';
                const payroll = preparePayrollData();
                headers = [['Teacher', 'Hours', 'Total Pay']];
                rows = payroll.map(p => [
                    p.teacher,
                    p.hours.toFixed(2),
                    `$${p.totalPay.toFixed(2)}`
                ]);
                break;
            }
            case 'ytdPayroll': {
                title = `Year-to-Date Payroll ${exportConfig.year}`;
                const ytd = prepareYTDPayrollData();
                headers = [['Teacher', 'Hours', 'Payable', 'Paid', 'Outstanding']];
                rows = ytd.map(p => [
                    p.teacher,
                    p.hours.toFixed(2),
                    `$${p.payable.toFixed(2)}`,
                    `$${p.paid.toFixed(2)}`,
                    `$${p.outstanding.toFixed(2)}`
                ]);
                break;
            }
            case 'parentPayments': {
                title = 'Parent Payments Report';
                const parents = prepareParentPaymentsData();
                headers = [['Parent', 'Month', 'Billed', 'Sessions']];
                rows = parents.map(p => [
                    p.parent,
                    p.month,
                    `$${p.billed.toFixed(2)}`,
                    p.sessions
                ]);
                break;
            }
            case 'monthlyBreakdown': {
                title = `Monthly Breakdown ${exportConfig.year}`;
                const monthly = prepareMonthlyBreakdown();
                headers = [['Month', 'Sessions', 'Hours', 'Revenue', 'Payroll', 'Profit']];
                rows = monthly.map(m => [
                    m.month,
                    m.sessions,
                    m.hours.toFixed(2),
                    `$${m.revenue.toFixed(2)}`,
                    `$${m.payroll.toFixed(2)}`,
                    `$${m.profit.toFixed(2)}`
                ]);
                break;
            }
            default:
                alert('Invalid export type');
                return;
        }

        // Add title
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(title, 14, 15);

        // Add date range info
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        let rangeText = '';
        if (exportConfig.dateRange === 'year') {
            rangeText = `Year: ${exportConfig.year}`;
        } else if (exportConfig.dateRange === 'month') {
            rangeText = `Month: ${exportConfig.month}`;
        } else if (exportConfig.dateRange === 'custom') {
            rangeText = `From: ${exportConfig.fromDate || 'All'} To: ${exportConfig.toDate || 'All'}`;
        } else {
            rangeText = 'All Time';
        }
        doc.text(rangeText, 14, 22);

        // Add table
        autoTable(doc, {
            head: headers,
            body: rows,
            startY: 28,
            theme: 'grid',
            headStyles: {
                fillColor: [26, 42, 108],
                textColor: [255, 255, 255],
                fontStyle: 'bold'
            },
            styles: {
                fontSize: 9,
                cellPadding: 3
            }
        });

        // Add totals if enabled
        if (exportConfig.includeTotals && rows.length > 0) {
            const finalY = doc.lastAutoTable.finalY + 5;
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Rows: ${rows.length}`, 14, finalY);
        }

        // Download
        const filename = `${title.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
        doc.save(filename);
    };

    // Handle export
    const handleExport = async () => {
        setIsExporting(true);
        try {
            if (exportConfig.format === 'excel') {
                exportToExcel();
            } else {
                exportToPDF();
            }
            alert('Export completed successfully!');
        } catch (err) {
            console.error('Export failed:', err);
            alert('Export failed. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Available years from data
    const availableYears = useMemo(() => {
        return Array.from(
            new Set(data.fees.map(f => parseLocalDate(f.date).getFullYear()))
        ).sort((a, b) => b - a);
    }, [data.fees]);

    return (
        <div className="page-container">
            <header className="content-header">
                <h2>Data Export</h2>
                <p>Configure and export your academy data to Excel or PDF</p>
            </header>

            <div style={{
                backgroundColor: 'var(--card-bg)',
                padding: '30px',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
                <div className="form-group">
                    <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                        Export Type
                    </label>
                    <select
                        value={exportConfig.exportType}
                        onChange={e => setExportConfig({ ...exportConfig, exportType: e.target.value })}
                    >
                        <option value="sessions">Student Sessions</option>
                        <option value="payroll">Payroll Summary</option>
                        <option value="ytdPayroll">Year-to-Date Payroll</option>
                        <option value="parentPayments">Parent Payments</option>
                        <option value="monthlyBreakdown">Monthly Financial Breakdown</option>
                    </select>
                </div>

                <div className="form-group">
                    <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                        Format
                    </label>
                    <select
                        value={exportConfig.format}
                        onChange={e => setExportConfig({ ...exportConfig, format: e.target.value })}
                    >
                        <option value="excel">Excel (.xlsx)</option>
                        <option value="pdf">PDF</option>
                    </select>
                </div>

                <div className="form-group">
                    <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                        Date Range
                    </label>
                    <select
                        value={exportConfig.dateRange}
                        onChange={e => setExportConfig({ ...exportConfig, dateRange: e.target.value })}
                    >
                        <option value="all">All Time</option>
                        <option value="year">Specific Year</option>
                        <option value="month">Specific Month</option>
                        <option value="custom">Custom Range</option>
                    </select>
                </div>

                {exportConfig.dateRange === 'year' && (
                    <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                            Year
                        </label>
                        <select
                            value={exportConfig.year}
                            onChange={e => setExportConfig({ ...exportConfig, year: Number(e.target.value) })}
                        >
                            {availableYears.map(y => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </div>
                )}

                {exportConfig.dateRange === 'month' && (
                    <div className="form-group">
                        <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                            Month
                        </label>
                        <input
                            type="month"
                            value={exportConfig.month}
                            onChange={e => setExportConfig({ ...exportConfig, month: e.target.value })}
                        />
                    </div>
                )}

                {exportConfig.dateRange === 'custom' && (
                    <>
                        <div className="form-group">
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                From Date
                            </label>
                            <input
                                type="date"
                                value={exportConfig.fromDate}
                                onChange={e => setExportConfig({ ...exportConfig, fromDate: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                To Date
                            </label>
                            <input
                                type="date"
                                value={exportConfig.toDate}
                                onChange={e => setExportConfig({ ...exportConfig, toDate: e.target.value })}
                            />
                        </div>
                    </>
                )}

                {/* Filters */}
                <div style={{
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', fontWeight: 600 }}>
                        Filters (Optional)
                    </h3>

                    {user.role === 'admin' && exportConfig.exportType === 'sessions' && (
                        <div className="form-group">
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                Teacher
                            </label>
                            <select
                                value={exportConfig.teacher}
                                onChange={e => setExportConfig({ ...exportConfig, teacher: e.target.value })}
                            >
                                <option value="">All Teachers</option>
                                {[...new Set(data.fees.map(f => f.teacher))].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {exportConfig.exportType === 'sessions' && (
                        <div className="form-group">
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                Student
                            </label>
                            <select
                                value={exportConfig.student}
                                onChange={e => setExportConfig({ ...exportConfig, student: e.target.value })}
                            >
                                <option value="">All Students</option>
                                {data.students.map(s => (
                                    <option key={s._id} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {(exportConfig.exportType === 'sessions' || exportConfig.exportType === 'parentPayments') && (
                        <div className="form-group">
                            <label style={{ fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                                Parent
                            </label>
                            <select
                                value={exportConfig.parent}
                                onChange={e => setExportConfig({ ...exportConfig, parent: e.target.value })}
                            >
                                <option value="">All Parents</option>
                                {[...new Set(data.students.map(s => s.parent))].map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Options */}
                <div style={{
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '1px solid #e5e7eb'
                }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '15px', fontWeight: 600 }}>
                        Export Options
                    </h3>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={exportConfig.includeTotals}
                                onChange={e => setExportConfig({ ...exportConfig, includeTotals: e.target.checked })}
                            />
                            Include totals row
                        </label>
                    </div>
                </div>

                {/* Export Button */}
                <div style={{ marginTop: '30px', display: 'flex', gap: '15px' }}>
                    <button
                        className="submit-btn"
                        onClick={handleExport}
                        disabled={isExporting}
                        style={{
                            padding: '14px 32px',
                            fontSize: '1rem',
                            minWidth: '150px'
                        }}
                    >
                        {isExporting ? 'Exporting...' : `Export to ${exportConfig.format.toUpperCase()}`}
                    </button>

                    <button
                        className="add-btn"
                        onClick={() => setExportConfig({
                            exportType: 'sessions',
                            format: 'excel',
                            dateRange: 'all',
                            year: currentYear,
                            month: '',
                            fromDate: '',
                            toDate: '',
                            teacher: '',
                            student: '',
                            parent: '',
                            includeDetails: true,
                            includeTotals: true,
                            groupBy: 'none'
                        })}
                        style={{ padding: '14px 32px' }}
                    >
                        Reset
                    </button>
                </div>

                {/* Preview Info */}
                <div style={{
                    marginTop: '30px',
                    padding: '15px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    fontSize: '0.9rem'
                }}>
                    <strong>Preview:</strong>
                    <div style={{ marginTop: '8px' }}>
                        {getFilteredData.length} records will be exported
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DataExport;